import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { catalogueApi, errorOf, needsMigration, ICONS, ICON_NAMES, FIELD_TYPE_LABEL, ROUTE_LABEL } from '../../lib/catalogue';
import { fmtDateTime } from '../../lib/when';
import ConfirmDialog from '../../components/ConfirmDialog';
import { ItemIcon } from './RequestCatalogue';
import './Catalogue.css';

/**
 * THE CATALOGUE, KEPT (migration 124) - Ticket Mgmt -> Tickets -> Catalogue,
 * beside Templates: an item is a kind of ticket made ready to ask for, with
 * the category, priority, template and approval every ticket has, so it is
 * kept where tickets are kept rather than in Settings. Administrators only,
 * like the server.
 *
 *   /manage/catalogue          every item, drafts too: on or off, who sees
 *                              it, its approval, how often it was asked for
 *   /manage/catalogue/new      a new one (a draft until switched on)
 *   /manage/catalogue/:id      one, to change; its history below
 *
 * The editor says what each part does in plain words; the server checks it
 * all again and its sentence is what is shown when something is refused.
 */
const blankItem = {
  name: '', description: '', icon: 'parcel', category_id: '', priority_id: '', position: '',
  active: false, audience: 'staff', audience_departments: [], people: [], contractors: false,
  base_cost: '', fields: [], template_id: '', approval_route: 'none', approver_id: '', approval_over: '',
  steps: [], actions: []
};
const newId = (p) => `${p}${Math.random().toString(36).slice(2, 8)}`;

function List() {
  const [items, setItems] = useState(null);
  const [problem, setProblem] = useState(null);
  useEffect(() => {
    let live = true;
    catalogueApi.adminList().then((r) => { if (live) setItems(r); })
      .catch((e) => { if (live) setProblem(needsMigration(e) ? 'The catalogue needs migration 124 (npm run migrate:124) on this server.' : errorOf(e, 'Could not load the catalogue.')); });
    return () => { live = false; };
  }, []);
  return (
    <div className="container rq-page" data-testid="catalogue-admin">
      <div className="page-header">
        <h1 className="page-title">Catalogue</h1>
        <p className="rq-lead">What staff (and, item by item, contractors) can ask for under Request something. Each item has its own form, cost, approval and steps. An item is a draft until you switch it on.</p>
        <p><Link className="btn btn-primary" to="/manage/catalogue/new">Add item</Link>{' '}<Link className="btn btn-plain" to="/request">See Request something</Link></p>
      </div>
      {problem && <div className="rq-alert" role="alert">{problem}</div>}
      {items && items.length === 0 && <div className="rq-empty">The catalogue is empty. Add the first item.</div>}
      {items && items.length > 0 && (
        <table className="rq-admin-table">
          <thead><tr><th scope="col">Item</th><th scope="col">State</th><th scope="col">Who sees it</th><th scope="col">Approval</th><th scope="col">Asked for</th></tr></thead>
          <tbody>
            {items.map((i) => (
              <tr key={i.id} data-item={i.name}>
                <td>
                  <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
                    <ItemIcon name={i.icon} />
                    <span><Link className="rq-admin-name" to={`/manage/catalogue/${i.id}`}>{i.name}</Link>
                      <div className="rq-row-sub">{[i.category_name, i.cost_words].filter(Boolean).join(' · ')}</div></span>
                  </div>
                </td>
                <td><span className="rq-stage" data-tone={i.active ? 'good' : 'draft'}>{i.active ? 'On' : 'Draft'}</span></td>
                <td>{i.audience_words}</td>
                <td>{ROUTE_LABEL[i.approval_route]}{i.approver_name ? ` - ${i.approver_name}` : ''}{i.approval_over !== null ? `, over €${Number(i.approval_over).toFixed(2)}` : ''}</td>
                <td>{i.requests}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Question({ q, i, count, onChange, onMove, onRemove }) {
  const set = (k, v) => onChange({ ...q, [k]: v });
  const opts = q.options || [];
  return (
    <div className="rq-q" data-question={i + 1}>
      <div className="rq-cols">
        <div className="rq-field"><label htmlFor={`q-${q.id}-label`}>Question</label>
          <input id={`q-${q.id}-label`} value={q.label} onChange={(e) => set('label', e.target.value)} /></div>
        <div className="rq-field"><label htmlFor={`q-${q.id}-type`}>Kind of answer</label>
          <select id={`q-${q.id}-type`} value={q.type} onChange={(e) => set('type', e.target.value)}>
            {Object.entries(FIELD_TYPE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select></div>
      </div>
      <div className="rq-field"><label htmlFor={`q-${q.id}-help`}>Help under it (optional)</label>
        <input id={`q-${q.id}-help`} value={q.help || ''} onChange={(e) => set('help', e.target.value)} /></div>
      <label className="rq-check"><input type="checkbox" checked={q.required === true} onChange={(e) => set('required', e.target.checked)} /> Needs an answer</label>
      {q.type === 'select' && (
        <div className="rq-field">
          <span className="rq-side-label">Choices, and what each adds to the cost</span>
          {opts.map((o, k) => (
            <div key={k} className="rq-cols">
              <input aria-label={`Choice ${k + 1}`} value={o.value} onChange={(e) => set('options', opts.map((x, j) => (j === k ? { ...x, value: e.target.value } : x)))} />
              <span style={{ display: 'flex', gap: '0.3rem' }}>
                <input aria-label={`Cost of choice ${k + 1}`} inputMode="decimal" placeholder="€ extra" value={o.cost ?? ''}
                       onChange={(e) => set('options', opts.map((x, j) => (j === k ? { ...x, cost: e.target.value } : x)))} />
                <button type="button" className="btn btn-plain btn-small" onClick={() => set('options', opts.filter((_, j) => j !== k))}>Remove</button>
              </span>
            </div>
          ))}
          <div><button type="button" className="btn btn-plain btn-small" onClick={() => set('options', [...opts, { value: '' }])}>Add a choice</button></div>
        </div>
      )}
      {q.type === 'number' && (
        <div className="rq-field"><label htmlFor={`q-${q.id}-unit`}>Cost of each (optional - makes it a quantity)</label>
          <input id={`q-${q.id}-unit`} inputMode="decimal" value={q.unit_cost ?? ''} onChange={(e) => set('unit_cost', e.target.value)} /></div>
      )}
      {q.type === 'checkbox' && (
        <div className="rq-field"><label htmlFor={`q-${q.id}-cost`}>Adds to the cost when ticked (optional)</label>
          <input id={`q-${q.id}-cost`} inputMode="decimal" value={q.cost ?? ''} onChange={(e) => set('cost', e.target.value)} /></div>
      )}
      <div className="rq-q-tools">
        <button type="button" className="btn btn-plain btn-small" disabled={i === 0} onClick={() => onMove(-1)}>Up</button>
        <button type="button" className="btn btn-plain btn-small" disabled={i === count - 1} onClick={() => onMove(1)}>Down</button>
        <button type="button" className="btn btn-plain btn-small" onClick={onRemove}>Remove question</button>
      </div>
    </div>
  );
}

const move = (list, i, d) => {
  const next = [...list];
  const j = i + d;
  if (j < 0 || j >= next.length) return next;
  [next[i], next[j]] = [next[j], next[i]];
  return next;
};

function Editor({ id }) {
  const navigate = useNavigate();
  const isNew = id === 'new';
  const [form, setForm] = useState(isNew ? blankItem : null);
  const [history, setHistory] = useState([]);
  const [requests, setRequests] = useState(0);
  const [opts, setOpts] = useState(null);
  const [problem, setProblem] = useState(null);
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let live = true;
    catalogueApi.options().then((o) => { if (live) setOpts(o); }).catch((e) => { if (live) setProblem(errorOf(e, 'Could not load the choices.')); });
    if (!isNew) {
      catalogueApi.adminItem(id).then((r) => {
        if (!live) return;
        const i = r.item;
        setForm({
          ...blankItem, ...i,
          category_id: i.category_id || '', priority_id: i.priority_id || '', template_id: i.template_id || '',
          approver_id: i.approver_id || '', base_cost: i.base_cost ?? '', approval_over: i.approval_over ?? '',
          description: i.description || '', icon: i.icon || 'parcel', people: i.people || []
        });
        setHistory(r.history || []);
        setRequests(i.requests || 0);
      }).catch((e) => { if (live) setProblem(errorOf(e, 'Could not load that item.')); });
    }
    return () => { live = false; };
  }, [id, isNew]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const personQs = useMemo(() => (form?.fields || []).filter((f) => f.type === 'person'), [form]);
  const licenceQs = useMemo(() => (form?.fields || []).filter((f) => f.type === 'licence'), [form]);

  const save = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setProblem(null); setSaved(null);
    const body = {
      ...form,
      category_id: form.category_id || null, priority_id: form.priority_id || null, template_id: form.template_id || null,
      approver_id: form.approver_id || null, base_cost: form.base_cost === '' ? null : form.base_cost,
      approval_over: form.approval_over === '' ? null : form.approval_over,
      position: form.position === '' || form.position === null ? undefined : Number(form.position)
    };
    ['id', 'requests', 'history', 'cost_words', 'audience_words', 'people_named', 'category_name', 'priority_name',
      'template_name', 'approver_name', 'seed_key', 'created_at', 'updated_at', 'currency'].forEach((k) => delete body[k]);
    if (body.position === undefined) delete body.position;
    try {
      const r = isNew ? await catalogueApi.create(body) : await catalogueApi.update(id, body);
      setHistory(r.history || []);
      setSaved('Saved.');
      if (isNew) navigate(`/manage/catalogue/${r.item.id}`, { replace: true });
    } catch (err) {
      setProblem(errorOf(err, 'That could not be saved.'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    try {
      await catalogueApi.remove(id);
      navigate('/manage/catalogue');
    } catch (err) {
      setDeleting(false);
      setProblem(errorOf(err, 'That could not be deleted.'));
    }
  };

  if (!form || !opts) {
    return <div className="container rq-page"><Link to="/manage/catalogue" className="rq-back">← Catalogue</Link>{problem ? <div className="rq-alert" role="alert">{problem}</div> : <p className="rq-lead">Loading…</p>}</div>;
  }

  return (
    <div className="container rq-page" data-testid="catalogue-editor">
      <Link to="/manage/catalogue" className="rq-back">← Catalogue</Link>
      <div className="page-header"><h1 className="page-title">{isNew ? 'Add item' : form.name || 'Item'}</h1></div>
      <form className="rq-editor" onSubmit={save} noValidate>
        <fieldset>
          <legend>The item</legend>
          <div className="rq-cols">
            <div className="rq-field"><label htmlFor="ce-name">Name</label>
              <input id="ce-name" value={form.name} onChange={(e) => set('name', e.target.value)} /></div>
            <div className="rq-field"><label htmlFor="ce-category">Ticket category</label>
              <select id="ce-category" value={form.category_id} onChange={(e) => set('category_id', e.target.value)}>
                <option value="">None</option>
                {opts.categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select></div>
            <div className="rq-field"><label htmlFor="ce-priority">Priority</label>
              <select id="ce-priority" value={form.priority_id} onChange={(e) => set('priority_id', e.target.value)}>
                <option value="">Default</option>
                {opts.priorities.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select></div>
            <div className="rq-field"><label htmlFor="ce-position">Order in the list</label>
              <input id="ce-position" inputMode="numeric" value={form.position ?? ''} onChange={(e) => set('position', e.target.value)} /></div>
          </div>
          <div className="rq-field"><label htmlFor="ce-description">What it is (shown on the card)</label>
            <textarea id="ce-description" rows={2} value={form.description} onChange={(e) => set('description', e.target.value)} /></div>
          <div className="rq-field"><span className="rq-side-label">Icon</span>
            <div className="rq-icons" role="group" aria-label="Icon">
              {ICON_NAMES.map((n) => (
                <button key={n} type="button" aria-label={n} aria-pressed={form.icon === n} onClick={() => set('icon', n)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d={ICONS[n]} /></svg>
                </button>
              ))}
            </div></div>
          <label className="rq-check"><input type="checkbox" checked={form.active} onChange={(e) => set('active', e.target.checked)} /> On - people can ask for it (off, it is a draft nobody sees)</label>
        </fieldset>

        <fieldset>
          <legend>Who sees it</legend>
          <div className="rq-field"><label htmlFor="ce-audience">Shown to</label>
            <select id="ce-audience" value={form.audience} onChange={(e) => set('audience', e.target.value)}>
              {opts.audiences.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select></div>
          {form.audience === 'departments' && (
            <div className="rq-field"><span className="rq-side-label">Departments</span>
              {opts.departments.map((d) => (
                <label key={d.id} className="rq-check"><input type="checkbox" checked={form.audience_departments.includes(d.name)}
                  onChange={(e) => set('audience_departments', e.target.checked ? [...form.audience_departments, d.name] : form.audience_departments.filter((x) => x !== d.name))} /> {d.name}</label>
              ))}
            </div>
          )}
          {form.audience === 'people' && (
            <div className="rq-field"><label htmlFor="ce-people">People (hold Ctrl or ⌘ to choose several)</label>
              <select id="ce-people" multiple size={6} value={form.people} onChange={(e) => set('people', Array.from(e.target.selectedOptions).map((o) => o.value))}>
                {opts.people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select></div>
          )}
          <label className="rq-check"><input type="checkbox" checked={form.contractors} onChange={(e) => set('contractors', e.target.checked)} /> Contractors too (in their portal)</label>
        </fieldset>

        <fieldset>
          <legend>The form</legend>
          <p className="rq-help">The questions asked. A choice can add to the cost; so can a number (a quantity) and a tick box.</p>
          {form.fields.map((q, i) => (
            <Question key={q.id} q={q} i={i} count={form.fields.length}
                      onChange={(nq) => set('fields', form.fields.map((x, j) => (j === i ? nq : x)))}
                      onMove={(d) => set('fields', move(form.fields, i, d))}
                      onRemove={() => set('fields', form.fields.filter((_, j) => j !== i))} />
          ))}
          <button type="button" className="btn btn-plain btn-small"
                  onClick={() => set('fields', [...form.fields, { id: newId('f'), label: '', type: 'text', required: false }])}>Add a question</button>
        </fieldset>

        <fieldset>
          <legend>Cost and ticket</legend>
          <div className="rq-cols">
            <div className="rq-field"><label htmlFor="ce-cost">Price (optional)</label>
              <input id="ce-cost" inputMode="decimal" placeholder="€" value={form.base_cost} onChange={(e) => set('base_cost', e.target.value)} /></div>
            <div className="rq-field"><label htmlFor="ce-template">Ticket template for the title and description (optional)</label>
              <select id="ce-template" value={form.template_id} onChange={(e) => set('template_id', e.target.value)}>
                <option value="">None - the item’s name and the answers</option>
                {opts.templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select></div>
          </div>
        </fieldset>

        <fieldset>
          <legend>Approval</legend>
          <div className="rq-cols">
            <div className="rq-field"><label htmlFor="ce-route">Who approves it</label>
              <select id="ce-route" value={form.approval_route} onChange={(e) => set('approval_route', e.target.value)}>
                {opts.routes.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select></div>
            {form.approval_route !== 'none' && form.approval_route !== 'requester' && (
              <div className="rq-field"><label htmlFor="ce-approver">{form.approval_route === 'manager' ? 'When there is no line manager who approves' : 'Approver'}</label>
                <select id="ce-approver" value={form.approver_id} onChange={(e) => set('approver_id', e.target.value)}>
                  <option value="">{form.approval_route === 'manager' ? 'Nobody - the requester picks' : 'Choose…'}</option>
                  {opts.approvers.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select></div>
            )}
            {form.approval_route !== 'none' && (
              <div className="rq-field"><label htmlFor="ce-over">Only when it costs more than (optional)</label>
                <input id="ce-over" inputMode="decimal" placeholder="€" value={form.approval_over} onChange={(e) => set('approval_over', e.target.value)} /></div>
            )}
          </div>
          <p className="rq-help">One approver, through the usual Approvals queue. A line manager who is not an approver in Jarvis is passed over, and the requester is told who decides instead.</p>
        </fieldset>

        <fieldset>
          <legend>Steps once it is approved</legend>
          <p className="rq-help">A checklist on the ticket. Each step is a to-do for its owner; the ticket cannot be resolved while one is open unless a reason is given.</p>
          {form.steps.map((s, i) => (
            <div key={s.id} className="rq-q" data-step={i + 1}>
              <div className="rq-cols">
                <div className="rq-field"><label htmlFor={`s-${s.id}`}>Step</label>
                  <input id={`s-${s.id}`} value={s.title} onChange={(e) => set('steps', form.steps.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))} /></div>
                <div className="rq-field"><label htmlFor={`s-${s.id}-owner`}>Done by</label>
                  <select id={`s-${s.id}-owner`} value={s.owner === 'person' ? s.owner_id || '' : ''}
                          onChange={(e) => set('steps', form.steps.map((x, j) => (j === i ? (e.target.value ? { ...x, owner: 'person', owner_id: e.target.value } : { ...x, owner: 'assignee', owner_id: undefined }) : x)))}>
                    <option value="">Whoever the ticket is assigned to</option>
                    {opts.people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select></div>
              </div>
              <div className="rq-field"><label htmlFor={`s-${s.id}-notes`}>Notes (optional)</label>
                <input id={`s-${s.id}-notes`} value={s.notes || ''} onChange={(e) => set('steps', form.steps.map((x, j) => (j === i ? { ...x, notes: e.target.value } : x)))} /></div>
              <div className="rq-q-tools">
                <button type="button" className="btn btn-plain btn-small" disabled={i === 0} onClick={() => set('steps', move(form.steps, i, -1))}>Up</button>
                <button type="button" className="btn btn-plain btn-small" disabled={i === form.steps.length - 1} onClick={() => set('steps', move(form.steps, i, 1))}>Down</button>
                <button type="button" className="btn btn-plain btn-small" onClick={() => set('steps', form.steps.filter((_, j) => j !== i))}>Remove step</button>
              </div>
            </div>
          ))}
          <button type="button" className="btn btn-plain btn-small" onClick={() => set('steps', [...form.steps, { id: newId('s'), title: '', owner: 'assignee' }])}>Add a step</button>
        </fieldset>

        <fieldset>
          <legend>What Jarvis does once it is approved</legend>
          {form.actions.map((a, i) => (
            <div key={i} className="rq-q" data-action={a.kind}>
              {a.kind === 'joiner' ? (
                <>
                  <div className="rq-field"><label htmlFor={`a-${i}`}>Start the joiner checklist for</label>
                    <select id={`a-${i}`} value={a.field || ''} onChange={(e) => set('actions', form.actions.map((x, j) => (j === i ? { ...x, field: e.target.value } : x)))}>
                      <option value="">Choose a “member of staff” question…</option>
                      {personQs.map((q) => <option key={q.id} value={q.id}>{q.label || 'Untitled question'}</option>)}
                    </select></div>
                  <label className="rq-check"><input type="checkbox" checked={a.standard_licences === true}
                    onChange={(e) => set('actions', form.actions.map((x, j) => (j === i ? { ...x, standard_licences: e.target.checked } : x)))} /> And give them the standard licences</label>
                </>
              ) : (
                <div className="rq-cols">
                  <div className="rq-field"><label htmlFor={`a-${i}`}>Give a seat of the licence in</label>
                    <select id={`a-${i}`} value={a.field || ''} onChange={(e) => set('actions', form.actions.map((x, j) => (j === i ? { ...x, field: e.target.value } : x)))}>
                      <option value="">Choose a “licence” question…</option>
                      {licenceQs.map((q) => <option key={q.id} value={q.id}>{q.label || 'Untitled question'}</option>)}
                    </select></div>
                  <div className="rq-field"><label htmlFor={`a-${i}-to`}>To</label>
                    <select id={`a-${i}-to`} value={a.person_field || ''} onChange={(e) => set('actions', form.actions.map((x, j) => (j === i ? { ...x, person_field: e.target.value || undefined } : x)))}>
                      <option value="">The requester</option>
                      {personQs.map((q) => <option key={q.id} value={q.id}>{q.label || 'Untitled question'}</option>)}
                    </select></div>
                </div>
              )}
              <div className="rq-q-tools"><button type="button" className="btn btn-plain btn-small" onClick={() => set('actions', form.actions.filter((_, j) => j !== i))}>Remove</button></div>
            </div>
          ))}
          <span className="rq-q-tools" style={{ justifyContent: 'flex-start' }}>
            <button type="button" className="btn btn-plain btn-small" onClick={() => set('actions', [...form.actions, { kind: 'joiner', field: personQs[0]?.id || '', standard_licences: true }])}>Start a joiner checklist</button>
            <button type="button" className="btn btn-plain btn-small" onClick={() => set('actions', [...form.actions, { kind: 'licence', field: licenceQs[0]?.id || '' }])}>Give a licence seat</button>
          </span>
        </fieldset>

        {problem && <div className="rq-alert" role="alert">{problem}</div>}
        {saved && <div className="rq-done" role="status">{saved}</div>}
        <div className="rq-q-tools" style={{ justifyContent: 'flex-start' }}>
          <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : isNew ? 'Add item' : 'Save'}</button>
          {!isNew && requests === 0 && <button type="button" className="btn btn-plain" onClick={() => setDeleting(true)}>Delete</button>}
          {!isNew && requests > 0 && <span className="rq-help">{`Asked for ${requests} time${requests === 1 ? '' : 's'}, so it is kept - switch it off instead of deleting it.`}</span>}
        </div>
      </form>

      {!isNew && history.length > 0 && (
        <section style={{ marginTop: '1.5rem' }}>
          <h2 className="rq-side-label">History</h2>
          <ul className="rq-history">
            {history.map((h) => <li key={h.id}><time>{fmtDateTime(h.at)}</time>{h.actor_name ? `${h.actor_name}: ` : ''}{h.said}</li>)}
          </ul>
        </section>
      )}
      {deleting && (
        <ConfirmDialog title={`Delete “${form.name}”?`} message="Nobody has asked for it, so nothing else changes." confirmLabel="Delete"
                       danger onConfirm={remove} onCancel={() => setDeleting(false)} />
      )}
    </div>
  );
}

export default function CatalogueAdmin() {
  const { itemId } = useParams();
  return itemId ? <Editor key={itemId} id={itemId} /> : <List />;
}
